import { useState } from 'react';
import { SortOption } from '@ktag/styleguide/dist/components/listactions/listactions.types';
import { Direction } from '@ktag/query-language';

import { usePollDays } from 'components/overview/Overview';
import { isPoll } from 'utils/poll';
import { useQueryParam } from '@ktag/styleguide/dist/react';
import { ISSUE_FILTER_TYPES } from 'utils/getIssueType';

const INITIAL_PAGE_SIZE = 18;
const ADDITIONAL_ISSUES_LOADED = 54;

const IDENTIFYING_STRING_GOVERNMENT_COUNCIL = 'Regierungsrat';

export const SORT_OPTIONS: SortOption[] = [
  {
    label: 'Neuste',
    value: {
      direction: Direction.desc,
      field: 'date',
    },
    type: 'numerical',
  },
  {
    label: 'Älteste',
    value: {
      direction: Direction.asc,
      field: 'date',
    },
    type: 'numerical',
  },
];

const useIssues = (selectedTypes: string[]) => {
  const { query } = usePollDays();
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [sort, setSort] = useState(SORT_OPTIONS[0]);
  const [term, setTerm]: [string, (initialValue: string) => void] = useQueryParam(
    'term',
    '',
  );

  const allIssues = query?.data?.data
    // first poll is not displayed in the archive
    ?.slice(1)
    .map(({ issues, date }) => issues.map((issue) => ({ ...issue, date })))
    .flat();

  const filteredIssuesWithType = allIssues.map((issue) => {
    let pollType: string;
    if (isPoll(issue.type)) {
      pollType = ISSUE_FILTER_TYPES.poll;
    } else if (issue.type === 'election_proportional_apportionment') {
      pollType = ISSUE_FILTER_TYPES.nationalCouncil;
    } else if (issue.type === 'election_majority_system') {
      if (issue.scope === 'cantonal') {
        if (issue.title?.de?.includes(IDENTIFYING_STRING_GOVERNMENT_COUNCIL)) {
          pollType = ISSUE_FILTER_TYPES.governmentCouncil;
        } else {
          pollType = ISSUE_FILTER_TYPES.councilOfStates;
        }
      } else {
        pollType = ISSUE_FILTER_TYPES.localElection;
      }
    } else if (issue.type === 'election_biproportional_apportionment') {
      pollType = ISSUE_FILTER_TYPES.grossRat;
    } else {
      pollType = 'other';
    }
    return { ...issue, pollType };
  });

  const filteredIssues = filteredIssuesWithType
    ?.filter((issue) => JSON.stringify(issue).toLowerCase().includes(term.toLowerCase()))
    .filter((issue) => {
      if (!selectedTypes.length) return true;
      return selectedTypes.includes(issue.pollType);
    });

  const sortedIssues = filteredIssues?.sort((a, b) => {
    const factor = sort.value.direction === Direction.asc ? 1 : -1;

    return a[sort.value.field] > b[sort.value.field] ? factor : -factor;
  });

  const numberOfResults = sortedIssues?.length ?? 0;
  const displayedIssues = sortedIssues.slice(0, pageSize).map(({ date, ...rest }) => ({
    ...rest,
    date,
  }));
  const hasMore = sortedIssues.length !== displayedIssues.length;

  const onLoadMore = () => {
    setPageSize(pageSize + ADDITIONAL_ISSUES_LOADED);
  };

  return {
    sort,
    setSort,
    numberOfResults,
    displayedIssues,
    hasMore,
    onLoadMore,
    term,
    setTerm,
  };
};

export default useIssues;
